import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Format large numbers for Y-axis display
const formatYAxisValue = (value) => {
  // Handle invalid values
  if (value === null || value === undefined || !isFinite(value)) return '0';
  if (value === 0) return '0';

  // Handle very large numbers (likely corrupted data)
  if (Math.abs(value) >= 1e15) return '0';

  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + 'M';
  }
  if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(0) + 'K';
  }
  return Math.round(value).toLocaleString();
};

const ChartJSBar = ({
  data,
  height = '300px',
  color = '#0D7463',
  title = '',
  responsive = true,
  isCurrency = false,
  valuePrefix = '',
  valueSuffix = ''
}) => {
  // Handle empty or invalid data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div style={{ height, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9CA3AF', fontSize: '14px' }}>
        No data available
      </div>
    );
  }

  // Sanitize data values to prevent corrupted/infinite values
  const sanitizeValue = (val) => {
    if (val === null || val === undefined || !isFinite(val) || Math.abs(val) >= 1e15) {
      return 0;
    }
    return val;
  };

  // Transform data for Chart.js
  const chartData = {
    labels: data.map(item => item.label),
    datasets: [
      {
        label: title,
        data: data.map(item => sanitizeValue(item.value)),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        hoverBackgroundColor: color,
        hoverBorderColor: color,
        hoverBorderWidth: 2,
        hoverBorderRadius: 6,
        maxBarThickness: 30, // Reduce bar width by half
      },
    ],
  };

  const options = {
    responsive: responsive,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll handle legend separately
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: color,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (context) => {
            return context[0].label;
          },
          label: (context) => {
            const value = context.parsed.y;
            const formattedValue = isCurrency
              ? `₦${value.toLocaleString()}`
              : `${valuePrefix}${value.toLocaleString()}${valueSuffix}`;
            return `${title}: ${formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B6B6B',
          font: {
            size: 11,
            weight: '500',
          },
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          display: true,
          color: '#F3F3F3',
          drawBorder: false,
        },
        ticks: {
          color: '#6B6B6B',
          font: {
            size: 11,
            weight: '500',
          },
          callback: function(value) {
            return formatYAxisValue(value);
          },
          maxTicksLimit: 6,
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <div style={{ height, width: '100%' }}>
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default ChartJSBar;
